import { useCallback, useEffect, useState } from 'react';

// The Facebook pages and Instagram accounts the current company can post to, with its saved
// default set. Keys look like "facebook:<page id>" / "instagram:<ig user id>" (SocialPostAccounts
// on the Rails side). Loads whenever `enabled` turns true, so a modal always sees fresh accounts.
export default function useSocialPostAccounts(enabled) {
  const [accounts, setAccounts] = useState([]);
  const [defaultKeys, setDefaultKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apply = useCallback((data) => {
    setAccounts(Array.isArray(data?.accounts) ? data.accounts : []);
    setDefaultKeys(Array.isArray(data?.default_keys) ? data.default_keys : []);
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/social_post_accounts', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!res.ok) throw new Error('social_post_accounts request failed');
      apply(await res.json());
      setError(null);
    } catch (_e) {
      setError("We couldn't load your connected accounts.");
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => {
    if (enabled) reload();
  }, [enabled, reload]);

  // Save the company default. Returns the server's error message, or null on success.
  const saveDefaults = useCallback(async (keys) => {
    const res = await fetch('/api/social_post_accounts/defaults', {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
      body: JSON.stringify({ keys }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return data?.error || 'Failed to save post targeting';
    apply(data);
    return null;
  }, [apply]);

  return { accounts, defaultKeys, loading, error, reload, saveDefaults };
}
